import { useLingui } from "@lingui/react";
import { IApiEpNotification } from "@smp/api";
import {
  UiBadge,
  UiButton,
  UiIconButton,
  UiLink,
  UiPopover,
  UiTypography,
} from "@smp/ui";
import { testAppendAttr } from "@smp/utils";
import { IconBellFilled } from "@tabler/icons-react";
import parse, {
  domToReact,
  Element,
  HTMLReactParserOptions,
} from "html-react-parser";
import { useCallback, useMemo, useState } from "react";
import { useUpdateEffect } from "react-use";

import { HelperAdapterLink } from "@/components/helpers/helper_adapter_link";
import { useEpNotification } from "@/hooks/use_ep_notification";
import { useContextApp } from "@/screens/app/context/app";

import { Notification } from "./_shared/notification/notification";
import { useStyles } from "./controls_notifications.css";
import { NotificationEmailVerify } from "./notification_email_verify";
import { NotificationSetPassword } from "./notification_set_password";

export const ControlsNotifications = () => {
  const {
    state: { me },
  } = useContextApp();

  const { i18n } = useLingui();

  const classes = useStyles();

  const [$anchor, set$Anchor] = useState<HTMLButtonElement | null>(null);

  const { notificationDestroy, notificationList } = useEpNotification();

  const notificationsAmount = useMemo(
    () =>
      (notificationList ? notificationList.length : 0) +
      (me.should_confirm_email ? 1 : 0) +
      (me.is_set_password || me.billing_policy_id === "elama" ? 0 : 1),
    [me, notificationList],
  );

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) =>
    set$Anchor(event.currentTarget);

  const handleClose = () => {
    set$Anchor(null);
  };

  const handleNotificationDelete = useCallback(
    (id: IApiEpNotification["id"]) => {
      notificationDestroy({ id });
    },
    [notificationDestroy],
  );

  const parserOptions: HTMLReactParserOptions = useMemo(
    () => ({
      replace: (domNode) => {
        if (
          domNode instanceof Element &&
          domNode.type === "tag" &&
          domNode.name === "a" &&
          domNode.attribs &&
          domNode.attribs.href &&
          domNode.children
        ) {
          return domNode.attribs.href.includes("http") ? (
            <UiLink
              href={domNode.attribs.href}
              onClick={handleClose}
              target={domNode.attribs.target || "_blank"}
            >
              {domToReact(domNode.children, parserOptions)}
            </UiLink>
          ) : (
            <UiLink
              component={HelperAdapterLink}
              onClick={handleClose}
              to={domNode.attribs.href.replace("/home", "")}
            >
              {domToReact(domNode.children, parserOptions)}
            </UiLink>
          );
        } else if (
          domNode instanceof Element &&
          domNode.type === "tag" &&
          domNode.name === "p" &&
          domNode.children
        ) {
          return (
            <UiTypography style={{ wordBreak: "break-word" }}>
              {domToReact(domNode.children, parserOptions)}
            </UiTypography>
          );
        }
      },
    }),
    [],
  );

  useUpdateEffect(() => {
    if (!notificationsAmount) {
      set$Anchor(null);
    }
  }, [notificationsAmount]);

  return useMemo(
    () =>
      notificationList && notificationsAmount ? (
        <>
          <UiIconButton
            {...testAppendAttr("id", `header_notifications`)}
            className={classes.button}
            onClick={handleClick}
          >
            <UiBadge
              badgeContent={notificationsAmount}
              color="secondary"
              classes={{ badge: classes.badge }}
              max={9}
              overlap="rectangular"
            >
              <IconBellFilled />
            </UiBadge>
          </UiIconButton>
          <UiPopover
            anchorEl={$anchor}
            anchorOrigin={{
              horizontal: "center",
              vertical: "bottom",
            }}
            classes={{
              paper: classes.popover_paper,
              root: classes.popover_root,
            }}
            onClose={handleClose}
            open={!!notificationsAmount && Boolean($anchor)}
            transformOrigin={{
              horizontal: "center",
              vertical: "bottom",
            }}
          >
            {!me.is_set_password && me.billing_policy_id !== "elama" && (
              <NotificationSetPassword onClose={handleClose} />
            )}
            {me.should_confirm_email && (
              <NotificationEmailVerify me={me} onClose={handleClose} />
            )}
            {notificationList.map((notification) => (
              <Notification
                actions={
                  <UiButton
                    color="primary"
                    onClick={() => handleNotificationDelete(notification.id)}
                    size="small"
                  >
                    OK
                  </UiButton>
                }
                body={
                  <>
                    {parse(
                      notification.body[i18n.locale] || notification.body.en,
                      parserOptions,
                    )}
                  </>
                }
                key={notification.id}
                level={notification.level}
                title={notification.title[i18n.locale] || notification.title.en}
              />
            ))}
          </UiPopover>
        </>
      ) : null,
    [
      $anchor,
      classes.badge,
      classes.button,
      classes.popover_paper,
      classes.popover_root,
      handleNotificationDelete,
      i18n.locale,
      me,
      notificationsAmount,
      notificationList,
      parserOptions,
    ],
  );
};
